/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * ClientComponent.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import {RouteComponentProps} from 'react-router-dom';
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Client from "../../models/Client";
import ClientService from "../../services/ClientService";
import ClientForm from "../../models/form/client/ClientForm";
import UIToast from "../../tools/view/toast/UIToast";
import AccessTokenService, {TokenSubjectType} from "../../services/AccessTokenService";
import AccessTokenDataProvider from "../../provider/AccessTokenDataProvider";
import List from "../../tools/list/List";
import AccessTokenAdapter from "../adapter/AccessTokenAdapter";
import HTTPService from "../../tools/service/HTTPService";
import {ListWatcher} from "../../tools/list/ListAdapter";
import {GrantType} from "../../tools/model/auth/GrantType";
import Colors from "../../tools/model/enumerations/Colors";
import UIError from "../../tools/view/toast/UIError";
import FormSection from "../../tools/view/form/FormSection";
import {ClientDTO} from "../../models/dto/client/ClientDTO";
import {FormListener} from "../../tools/model/form/AbstractForm";
import Translation from "../../services/translation/Translation";
import DeviceDataProvider from "../../provider/DeviceDataProvider";
import DeviceAdapter, {DeviceAdapterListener} from "../adapter/DeviceAdapter";
import DeviceService from "../../services/DeviceService";
import FormModal from "../../tools/view/layout/FormModal";
import ReactDOM from "react-dom";
import DeviceForm from "../../models/form/device/DeviceForm";
import Device from "../../models/Device";
import UIAlert from "../../tools/view/toast/UIAlert";
import PageComponent from "../../tools/view/layout/PageComponent";
import FormInput, {Type} from "../../tools/view/form/FormInput";
import {TextField} from "../../tools/model/form/TextField";
import AdminClientClientEditor from "../view/client/AdminClientClientEditor";
import ClientActivationForm from "../../models/form/client/ClientActivationForm";
import {ClientActivationDTO} from "../../models/dto/client/ClientActivationDTO";


interface Props extends RouteComponentProps<MatchParams> {
}

interface MatchParams {
    id: string;
}

interface State {
    policy: Policy;
    pageIndex: number,
    client?: Client;
    tokenDataProvider: AccessTokenDataProvider;
    tokenAdapter: AccessTokenAdapter;
    deviceDataProvider: DeviceDataProvider;
    deviceAdapter: DeviceAdapter;
    clientForm: ClientForm;
    clientActivationForm: ClientActivationForm;
    deviceForm: DeviceForm;

}

export default class ClientPage extends HiloReactComponent<Props, State> implements PolicyListener, ListWatcher, DeviceAdapterListener, FormListener<ClientForm, ClientDTO>, FormListener<ClientActivationForm, ClientActivationDTO> {

    private readonly _tokenService: AccessTokenService;
    private readonly _clientService: ClientService;
    private readonly _deviceService: DeviceService;
    private readonly _httpService: HTTPService;
    private _modalContainer?: Element;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: Props, state: State) {
        super(props, state);

        this._tokenService = this._serviceRepository.resolve(AccessTokenService.id) as AccessTokenService
        this._clientService = this._serviceRepository.resolve(ClientService.id) as ClientService
        this._deviceService = this._serviceRepository.resolve(DeviceService.id) as DeviceService
        this._httpService = this._serviceRepository.resolve(HTTPService.id) as HTTPService

        const tokenProvider = new AccessTokenDataProvider(this._tokenService, this._httpService, TokenSubjectType.CLIENT, this.props.match.params.id);
        const deviceProvider = new DeviceDataProvider(this._deviceService, this._httpService);

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            client: undefined,
            tokenDataProvider: tokenProvider,
            tokenAdapter: new AccessTokenAdapter(this.t, tokenProvider),
            clientForm: new ClientForm(Object.values(GrantType)),
            clientActivationForm: new ClientActivationForm(),
            deviceForm: new DeviceForm(),
            deviceDataProvider: deviceProvider,
            deviceAdapter: new DeviceAdapter(this.t, deviceProvider, this)
        };
    }

    /**
     * onUpdate
     */
    public onUpdate(): void {
        this.forceUpdate();
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._policyService.addListener(this);

        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this.state.clientForm.addListener(this)
        this.state.clientActivationForm.addListener(this)

        this.state.tokenDataProvider.subscribe(this);

        this._refreshClient();
    }

    /**
     * _refreshClient
     */
    private _refreshClient = async () => {
        let client = await this._clientService.findById(this.props.match.params.id);

        await this.state.clientForm.load(ClientDTO.fromClient(client));
        await this.state.clientActivationForm.load(ClientActivationDTO.fromClient(client))

        await this.setState({client: client});

        this.state.tokenDataProvider.tokenSubjectId = client.id;
        this.state.deviceDataProvider.client = client
    };

    public componentWillUnmount(): void {
        this.state.clientForm.removeListener(this);
        this.state.clientActivationForm.removeListener(this);
        this._policyService.removeListener(this);
        super.componentWillUnmount();
    }

    /**
     * componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            this.state.tokenDataProvider.tokenSubjectId = this.props.match.params.id;
            this._refreshClient();
        }
    }


    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _displayAddDeviceModal
     * @private
     */
    private _displayAddDeviceModal = async () => {
        if (!this._modalContainer || !this.state.deviceForm) {
            return;
        }

        const modal = <FormModal
            emptyForm={this.state.deviceForm}
            title={t => t.newDevice}
            onSaveClicked={this._saveDevice}
        />;

        ReactDOM.render(modal, this._modalContainer);
    }

    /**
     * _clientView
     * @returns {any}
     * @private
     */
    private _clientView = (props: { form: ClientForm, client?: Client }) => {
        let clientIdField = new TextField("client_id", t => t.clientId, _ => true, Type.TEXT, false, props.client?.client_id, "", true)
        let clientSecretField = new TextField("client_secret", t => t.clientSecret, _ => true, Type.TEXT, false, props.client?.client_secret, "", true)

        return <>
            <FormInput field={clientIdField}/>
            <FormInput field={clientSecretField}/>
            <FormSection label={this.t.identity} form={props.form} submit={this._saveClient}/>
        </>
    };

    /**
     * _clientActivationView
     * @returns {any}
     * @private
     */
    private _clientActivationView = (props: { form: ClientActivationForm, client?: Client }) => {
        return <>
            <FormSection label={this.t.activation} form={props.form} submit={this._saveClientActivation}/>
        </>
    };

    /**
     * _devicesView
     * @returns {any}
     * @private
     */
    private _devicesView = () => {
        const {deviceAdapter, deviceDataProvider} = this.state;

        return <List adapter={deviceAdapter} dataProvider={deviceDataProvider}
                     onAddClick={this._displayAddDeviceModal}/>
    };

    /**
     * _saveClient
     * @param dto
     * @private
     */
    private _saveClient = async (dto: FormData) => {
        const {client} = this.state;
        if (!client) return console.log("client is undefined.");

        try {
            await this._clientService.updateClient(client, dto);
            this.state.clientForm.statusCode = 200;
            UIToast.default.show(this.t.clientEdited)
            await this._refreshClient();
        } catch (e) {
            this.state.clientForm.statusCode = e.status
            console.error(e);
        }
    };

    /**
     * _saveClientActivation
     * @param dto
     * @private
     */
    private _saveClientActivation = async (dto: FormData) => {
        const {client} = this.state;
        if (!client) return console.log("client is undefined.");

        try {
            await this._clientService.setActivation(client, dto);
            this.state.clientActivationForm.statusCode = 200;
            UIToast.default.show(this.t.clientEdited)
            await this._refreshClient();
        } catch (e) {
            this.state.clientActivationForm.statusCode = e.status
            console.error(e);
        }
    };

    /**
     * _saveDevice
     * @param dto
     * @private
     */
    private _saveDevice = async (dto: FormData) => {
        const {client} = this.state;
        if (!client) return console.log("client is undefined.");

        try {
            await this._deviceService.createDevice(client, dto);
            this.state.deviceForm.statusCode = 200;
            UIToast.default.show(this.t.deviceCreated)
            await this._refreshClient();
        } catch (e) {
            this.state.clientForm.statusCode = e.status
            console.error(e);
        }
    };

    /**
     * tokenList
     * @returns {any}
     * @private
     */
    private _tokenList = () => {
        const {tokenAdapter, tokenDataProvider} = this.state;

        return <List adapter={tokenAdapter}
                     dataProvider={tokenDataProvider}
                     accessoryButton={{
                         labelAccessor: (t: Translation) => t.revoke,
                         color: Colors.red,
                         onClick: this._revokeTokens
                     }}
        />
    };

    /**
     * _revokeTokens
     * @private
     */
    private _revokeTokens = () => {
        if (!this.state.client) {
            return;
        }

        try {
            this._tokenService.revokeByClient(this.state.client);
            this.state.tokenDataProvider.load();
            UIToast.default.show(this.t.revoked);
        } catch (e) {
            UIError.default.show("Failed to revoke tokens.");
        }
    }

    /**
     * onFormUpdate
     * @param form
     */
    public onFormUpdate = (form: ClientForm | ClientActivationForm) => {
        if (form instanceof ClientForm) {
            this.setState({clientForm: form})
        } else {
            this.setState({clientActivationForm: form})
        }
    };

    /**
     * device list callback
     * removeDevice
     * @param device
     */
    public async removeDevice(device: Device) {
        const {t} = this;

        if (!this.state.client) return;

        const confirm = await UIAlert.default.show(t, t.deviceRemoveConfirmation);
        ;
        if (!confirm) return

        try {
            await this._deviceService.deleteById(this.state.client, device)
        } catch (e) {
            UIError.default.show(e.statusCode)
            return;
        }

        UIToast.default.show(this.t.UserComponentRemoveUserConfirm);
        this.state.deviceDataProvider.load();
    }

    /**
     * rowSelected
     * @param obj
     */
    public rowSelected(obj: Device) {
    }

    /**
     * render
     */
    public render(): any {
        const {client, clientForm, clientActivationForm} = this.state;
        const titleTabs: TitleTab[] = [
            {
                title: t => t.details, content: <>
                    <this._clientView form={clientForm} client={client}/>
                    <this._clientActivationView form={clientActivationForm} client={client}/>
                </>
            },
            {title: t => t.devices, content: <this._devicesView/>},
            {
                title: t => t.clients,
                content: <AdminClientClientEditor client={client!!} clientUpdated={this._refreshClient}/>
            },
            {title: t => t.token, content: <this._tokenList/>}
        ];

        return <PageComponent>
            <PageTitle title={t => t.clients}
                       tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}
                       subTitle={client?.name || this.t.loading}
                       backLabel={t => t.allClients}
            />
            {titleTabs[this.state.pageIndex].content}
        </PageComponent>
    }
}
